import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useQuery } from '@apollo/client';
import { generateLogVolumeGraphRequest, LRQRequest } from 'hooks/useLRQ/useLRQ';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { QueryRequestInput, QueryType } from 'common/graphqlTypes';
import * as K from 'common/constants';
import { getRequestId } from 'common/util/lrqRequest';
import LRQRequestManager from 'components/common/Managers/LRQRequestManager';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import { defaultStartTime, searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { SOURCES_QUERY } from './queries';
import LogOverviewSourcesTab from './LogOverviewSourcesTab';

const LogOverviewSourcesContainer = (): JSX.Element => {
  const defaultStart = useRecoilValue(defaultStartTime);
  const showOld = useRecoilValue(searchParamState(K.URL_PARAM.SHOW_OLD));
  const [logVolumeGraphRequest, setLogVolumeGraphRequest] = useState<LRQRequest | null>(null);
  const id = getRequestId(logVolumeGraphRequest, QueryType.PLOT);
  const { results, errors } = useEventCache([id]);
  const { data, loading, error } = useQuery(SOURCES_QUERY, { variables: { showOld: !!showOld } });

  useEffect(() => {
    const request = generateLogVolumeGraphRequest({
      startTime: defaultStart,
      endTime: '',
    } as unknown as QueryRequestInput);

    setLogVolumeGraphRequest(request);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLRQDone = results?.[0]?.isDone;

  const renderLogOverviewSourcesTab = (): JSX.Element => {
    if (loading || !isLRQDone) {
      return <LoadingPlaceholder />;
    }

    const firstLRQError = errors?.[0];
    if (error || firstLRQError) {
      const lrqError = { message: firstLRQError?.terminationReasons?.[0] || '' };
      return <ErrorPlaceholder error={error || lrqError} />;
    }

    return <LogOverviewSourcesTab sources={data.sources} results={results} />;
  };

  const logVolumeLRQManager = logVolumeGraphRequest ? (
    <LRQRequestManager key={logVolumeGraphRequest.key} request={logVolumeGraphRequest} />
  ) : null;

  return (
    <>
      {renderLogOverviewSourcesTab()}
      {logVolumeLRQManager}
    </>
  );
};

export default LogOverviewSourcesContainer;
